import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import { useEffect } from 'react';
import FAQSection from '../components/FAQSection';

const HelpCenter = () => {
  const [searchQuery, setSearchQuery] = useState('');
  const [openFaq, setOpenFaq] = useState(null);

  const categories = [
    {
      icon: '',
      title: 'Getting Started',
      description: 'New to SERPfinity? Start here.',
      articles: [
        { title: 'How to create your account', content: 'Visit thedigitalhall.club and click "Start Free Trial" or "Register". Fill in your name, email, and create a password. You will receive a verification email to confirm your account.' },
        { title: 'Running your first AI visibility scan', content: 'From your dashboard, enter a domain URL in the scan bar and click "Get Free AI Score". Our AI will analyze the site across 20+ SERP features and AI search engines, delivering results in about 60 seconds.' },
        { title: 'Understanding your dashboard', content: 'Your dashboard provides an overview of your scans, tracked keywords, domain watchlist, and recent reports. The sidebar lets you navigate between all the SEO tools available in your plan.' },
        { title: 'Setting up your first keyword tracking', content: 'Navigate to the Rank Tracking section, enter the keywords you want to monitor, select your target location and search engine, and SERPfinity will begin tracking your rankings daily.' }
      ]
    },
    {
      icon: '',
      title: 'AI Search Visibility',
      description: 'Understand your AI search presence.',
      articles: [
        { title: 'What is AI Search Visibility?', content: 'AI Search Visibility measures how well your content appears in AI-powered search engines like ChatGPT, Google AI Overview, Perplexity, and more. SERPfinity tracks your presence across all major AI platforms.' },
        { title: 'How AI visibility scores are calculated', content: 'Your AI visibility score is based on multiple factors including citation frequency in AI responses, content quality signals, entity recognition, and how often your brand or content is referenced across AI search platforms.' },
        { title: 'Improving your AI search rankings', content: 'Focus on creating comprehensive, well-structured content. Use clear headings, answer common questions directly, include structured data markup, and ensure your site is technically sound for AI crawlers.' },
        { title: 'Tracking competitors in AI search', content: 'Use the Competitor Analysis tool to monitor how competitors rank in AI search results. Compare visibility scores, identify content gaps, and discover opportunities to outperform them.' }
      ]
    },
    {
      icon: '',
      title: 'SEO Tools & Reports',
      description: 'Make the most of our analysis tools.',
      articles: [
        { title: 'SERP Analysis explained', content: 'Our SERP Analysis tool breaks down search results for any keyword, showing all SERP features present including featured snippets, knowledge panels, People Also Ask, image packs, and more.' },
        { title: 'Using the Content Optimizer', content: 'The Content Optimizer analyzes your content against top-ranking pages and provides actionable recommendations for improving relevance, readability, keyword usage, and content structure.' },
        { title: 'Backlink Explorer overview', content: 'Discover your backlink profile, identify new link opportunities, monitor competitor links, and track your link-building progress over time with our Backlink Explorer.' },
        { title: 'Generating and exporting reports', content: 'Navigate to the Reports section to generate detailed PDF reports of your SEO metrics. Reports can be customized, branded, and scheduled for automatic delivery to your inbox.' }
      ]
    },
    {
      icon: '',
      title: 'Billing & Plans',
      description: 'Manage your subscription and payments.',
      articles: [
        { title: 'Available subscription plans', content: 'SERPfinity offers Starter ($17/mo), Business ($39/mo), and Enterprise ($79/mo) plans. Each includes different scan limits, tool access, and features. All plans include a free trial period.' },
        { title: 'Upgrading or downgrading your plan', content: 'Go to Profile > Subscription to change your plan. Upgrades take effect immediately with prorated billing. Downgrades take effect at the end of your current billing cycle.' },
        { title: 'Canceling your subscription', content: 'You can cancel anytime from your Profile page. Your access continues until the end of your current billing period. No partial refunds are issued for unused time.' },
        { title: 'Payment methods and invoices', content: 'We accept all major credit cards through our secure payment processor (Stripe). Invoices are emailed automatically after each payment and can also be accessed from your Profile page.' }
      ]
    },
    {
      icon: '',
      title: 'Technical Support',
      description: 'Troubleshooting and technical help.',
      articles: [
        { title: 'Scan not completing or showing errors', content: 'If a scan fails, check that the URL is valid and the site is accessible. Some sites may block scanning bots. Try again in a few minutes, or contact support if the issue persists.' },
        { title: 'Account login issues', content: 'If you cannot log in, try the "Forgot Password" link on the login page to reset your password. Make sure you are using the correct email address. Clear your browser cache if issues persist.' },
        { title: 'Browser compatibility', content: 'SERPfinity works best on the latest versions of Chrome, Firefox, Safari, and Edge. Please ensure your browser is up to date and JavaScript is enabled.' },
        { title: 'API access and documentation', content: 'API access is available on Business and Enterprise plans. API documentation and your API key can be found in your Profile > API Settings. Rate limits vary by plan.' }
      ]
    }
  ];

  const faqs = [
    { q: 'What is SERPfinity?', a: 'SERPfinity is an AI-powered search visibility platform that helps you track and optimize your presence across both traditional search engines and AI-powered search platforms like ChatGPT, Google AI Overview, and Perplexity.' },
    { q: 'Is there a free trial?', a: 'Yes! All new accounts include a free trial period where you can explore all features. No credit card is required to start your trial.' },
    { q: 'How often are rankings updated?', a: 'Rank tracking data is updated daily. AI visibility scans can be run on-demand anytime within your plan limits.' },
    { q: 'Can I track multiple websites?', a: 'Yes, you can track multiple domains depending on your plan. The Business and Enterprise plans support multiple websites and team collaboration features.' },
    { q: 'Do you offer refunds?', a: 'We offer refunds on a case-by-case basis within the first 14 days of a paid subscription. Please contact our support team for assistance.' },
    { q: 'How do I contact support?', a: 'You can reach us through our Contact Us page, or email us directly at info@serpfinity.ai. We typically respond within 24-48 business hours.' }
  ];

  const filteredCategories = searchQuery
    ? categories.map(cat => ({
        ...cat,
        articles: cat.articles.filter(a =>
          a.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
          a.content.toLowerCase().includes(searchQuery.toLowerCase())
        )
      })).filter(cat => cat.articles.length > 0)
    : categories;


  const HELP_FAQ = [
    { q: "How do I get started with SERPfinity?", a: "Create your account, add your first project with a domain, add target keywords, run your first site audit, and explore the dashboard. Most users get insights within 15 minutes." },
    { q: "How do I contact support?", a: "Use in-app chat for real-time help, submit support tickets for detailed inquiries, or browse our knowledge base for self-service guides." },
    { q: "Where can I find tutorials?", a: "Comprehensive tutorials with screenshots and videos are organized by feature in the Help Center and regularly updated." },
    { q: "Is there a community forum?", a: "SERPfinity is building a user community with blog insights, webinars, and social media discussions about search optimization." },
    { q: "How do I report bugs or request features?", a: "Use the in-app feedback button for bug reports or email our product team with feature suggestions." },
    { q: "What browsers are supported?", a: "SERPfinity works on Chrome, Firefox, Safari, and Edge. Fully responsive for desktop, tablet, and mobile devices." }
  ];
  // Schema injection via useEffect
  useEffect(() => {
    const schemas = [{"@context":"https://schema.org","@type":"FAQPage","mainEntity":HELP_FAQ.map(item=>({"@type":"Question","name":item.q,"acceptedAnswer":{"@type":"Answer","text":item.a}}))}];
    const scriptElements = [];
    schemas.forEach(schema => {
      const script = document.createElement('script');
      script.type = 'application/ld+json';
      script.textContent = JSON.stringify(schema);
      document.head.appendChild(script);
      scriptElements.push(script);
    });
    return () => {
      scriptElements.forEach(el => el.remove());
    };
  }, []);


  return (
    <>
      <Helmet>
        <title>Help Center & Documentation | SERPfinity Support</title>
        <meta name="description" content="Get help with SERPfinity tools. Browse documentation, tutorials, and contact support." />
        <meta name="keywords" content="SERPfinity help center, support, documentation, tutorials, FAQ" />
        <link rel="canonical" href="https://thedigitalhall.club/help" />
        <meta name="robots" content="index, follow" />
            <meta property="og:title" content="Help Center & Documentation | SERPfinity Support" />
            <meta property="og:description" content="Get help with SERPfinity tools. Browse documentation, tutorials, and contact support." />
            <meta property="og:type" content="website" />
            <meta property="og:url" content="https://app.serpfinity.ai/help" />
            <meta property="og:image" content="https://app.serpfinity.ai/og-image.png" />
            <meta property="og:site_name" content="SERPfinity" />
            <meta name="twitter:card" content="summary_large_image" />
            <meta name="twitter:title" content="Help Center & Documentation | SERPfinity Support" />
            <meta name="twitter:description" content="Get help with SERPfinity tools. Browse documentation, tutorials, and contact support." />
            <meta name="twitter:image" content="https://app.serpfinity.ai/og-image.png" />

      </Helmet>
    <div className="min-h-screen bg-white">
      {/* Hero Section */}
      <div className="bg-gradient-to-br from-slate-900 via-blue-900 to-slate-900 text-white py-20">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-4xl md:text-5xl font-bold mb-4">Help Center</h1>
          <p className="text-lg text-blue-200 mb-8">
            Find answers, guides, and support for everything SERPfinity.
          </p>
          <div className="max-w-xl mx-auto">
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search for help articles..."
              className="w-full px-6 py-4 rounded-xl text-slate-900 text-lg focus:ring-4 focus:ring-blue-300 outline-none shadow-lg"
            />
          </div>
        </div>
      </div>

      {/* Categories */}
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        {searchQuery && (
          <p className="text-slate-500 mb-8">
            Showing results for "<span className="font-semibold text-slate-700">{searchQuery}</span>"
            {' '}({filteredCategories.reduce((acc, c) => acc + c.articles.length, 0)} articles found)
          </p>
        )}

        <div className="space-y-12">
          {filteredCategories.map((category, idx) => (
            <div key={idx}>
              <div className="flex items-center gap-3 mb-6">
                <span className="text-3xl">{category.icon}</span>
                <div>
                  <h2 className="text-2xl font-bold text-slate-900">{category.title}</h2>
                  <p className="text-slate-500">{category.description}</p>
                </div>
              </div>
              <div className="grid sm:grid-cols-2 gap-4">
                {category.articles.map((article, aIdx) => (
                  <details key={aIdx} className="group bg-slate-50 rounded-lg border border-slate-200 overflow-hidden">
                    <summary className="flex items-center justify-between px-6 py-4 cursor-pointer hover:bg-slate-100 transition font-medium text-slate-800">
                      {article.title}
                      <svg className="w-5 h-5 text-slate-400 group-open:rotate-180 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                      </svg>
                    </summary>
                    <div className="px-6 pb-4 text-slate-600 leading-relaxed">
                      {article.content}
                    </div>
                  </details>
                ))}
              </div>
            </div>
          ))}
        </div>

        {filteredCategories.length === 0 && (
          <div className="text-center py-16">
            <p className="text-xl text-slate-500 mb-4">No articles found matching your search.</p>
            <button onClick={() => setSearchQuery('')} className="text-blue-600 hover:text-blue-800 font-medium">Clear search</button>
          </div>
        )}

        {/* FAQ Section */}
        {!searchQuery && (
          <div className="mt-20">
            <h2 className="text-3xl font-bold text-slate-900 text-center mb-10">Frequently Asked Questions</h2>
            <div className="max-w-3xl mx-auto space-y-3">
              {faqs.map((faq, idx) => (
                <div key={idx} className="border border-slate-200 rounded-lg overflow-hidden">
                  <button
                    onClick={() => setOpenFaq(openFaq === idx ? null : idx)}
                    className="w-full flex items-center justify-between px-6 py-4 text-left font-medium text-slate-800 hover:bg-slate-50 transition"
                  >
                    {faq.q}
                    <svg className={"w-5 h-5 text-slate-400 transition-transform " + (openFaq === idx ? "rotate-180" : "")} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                    </svg>
                  </button>
                  {openFaq === idx && (
                    <div className="px-6 pb-4 text-slate-600 leading-relaxed">{faq.a}</div>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Contact CTA */}
        <div className="mt-20 text-center p-12 bg-gradient-to-r from-blue-50 to-indigo-50 rounded-2xl border border-blue-100">
          <h3 className="text-2xl font-bold text-slate-900 mb-3">Still need help?</h3>
          <p className="text-slate-600 mb-6">Our support team is ready to assist you.</p>
          <Link to="/contact" className="inline-block px-8 py-3 bg-blue-600 text-white font-semibold rounded-lg hover:bg-blue-700 transition">
            Contact Support
          </Link>
        </div>
      </div>
    </div>
      <FAQSection items={HELP_FAQ} />
    </>
  );
};

export default HelpCenter;
